// Phase 6 — Track D T-D2 / T-D5. Date formatting utility.
//
// Per blueprint section 13.4, dates within 7 days of "today" render
// relative-to-today ("Today", "Tomorrow", "In 3 days", "3 days ago"); dates
// beyond render absolute via Intl.DateTimeFormat ("12 May 2026"). The
// user's timezone is honoured implicitly because both inputs are
// interpreted as plain calendar dates (YYYY-MM-DD strings; the time
// component is irrelevant at v1).
//
// Pure function. No I/O. Single English locale at v1.

const ABSOLUTE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function toCalendarDay(input: string | Date): Date | null {
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null;
    return new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()));
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function dayDiff(target: Date, base: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round((target.getTime() - base.getTime()) / oneDay);
}

export function formatDate(
  date: string | Date | null | undefined,
  today: Date = new Date(),
): string {
  if (date === null || date === undefined) return '';
  const target = toCalendarDay(date);
  if (target === null) return '';
  const base = toCalendarDay(today);
  if (base === null) return '';

  const diff = dayDiff(target, base);

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff >= 2 && diff <= 7) return `In ${diff} days`;
  if (diff <= -2 && diff >= -7) return `${Math.abs(diff)} days ago`;

  return ABSOLUTE_FORMATTER.format(target);
}
